// DEV-ONLY · GEFAHRSTOFF-BÜHNE (Preview) — Betreiberfreigabe 29.09.2026.
//
// Baut die Gefahrstoff-Produktgeschichte im EINGEFRORENEN Visual Standard
// (B1/B2/B4): fotorealistischer CL-Monitor als primärer Produktträger,
// Chris groß/präsent/richtungsrichtig als Figurengruppe, helle CI, eigene
// Mobile-Komposition. Live `/abo` bleibt UNBERÜHRT bis zur Sichtabnahme.
//
// Produktwahr (forensisch gegen den echten Code geprüft, 29.09.2026):
//  · Chris 1 (Kette) und Chris 2 (SDB) sind wortwörtlich betreiberfreigegeben.
//  · Vier ECHTE Screenshots als verbindliche Beweise, keine Fake-UI:
//    Suche „Sterilium" (im CL-Monitor), Lehrbilder/Sicherheitszeichen,
//    Gefahrstoffverzeichnis als PDF, Gefahrstoffverzeichnis-UI (im CL-Monitor).
//  · KEIN Handschutzplan hier (bewusst weggelassen — Klarheit).
import { useEffect, useRef } from "react";
import AboChris from "@/components/abo/AboChris";
import "@/pages/abo/abo.css";

const DEV = (n) => `/abo/devices/${n}`;
const SHOT = (n) => `/abo/screenshots/${n}`;
const OUTFIT =
  "https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap";

export default function AboGefahrstoffDevPreview() {
  const ref = useRef(null);
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = OUTFIT;
    document.head.appendChild(link);
    ref.current?.querySelectorAll(".reveal").forEach((el) => el.classList.add("in"));
    return () => link.remove();
  }, []);

  return (
    <div
      className="abo-root"
      lang="de"
      ref={ref}
      data-testid="abo-gefahrstoff-dev"
      style={{ minHeight: "100vh" }}
    >
      <style>{`
        [data-testid="abo-gefahrstoff-dev"] .reveal{opacity:1 !important;transform:none !important;}
        [data-testid="abo-gefahrstoff-dev"] .dev-label{max-width:1280px;margin:0 auto;padding:22px 24px 0;font:600 .72rem/1.4 "Outfit",sans-serif;letter-spacing:.14em;text-transform:uppercase;color:#94a3b8;}

        /* Bühne · helle, freundliche CI (eigener Farbklang, distinct zu B1/B2/B4) */
        [data-testid="abo-gefahrstoff-dev"] .gh-stage{background:radial-gradient(120% 120% at 12% -10%, #fef6ee 0%, #f4f4ef 46%, #eef5f4 100%);padding:60px 0 72px;}
        [data-testid="abo-gefahrstoff-dev"] .gh-wrap{max-width:1280px;margin:0 auto;padding:0 24px;}

        /* Kopf */
        [data-testid="abo-gefahrstoff-dev"] .gh-head{max-width:820px;margin:0 0 40px;}
        [data-testid="abo-gefahrstoff-dev"] .gh-meta{display:flex;align-items:center;gap:12px;margin-bottom:14px;}
        [data-testid="abo-gefahrstoff-dev"] .gh-kicker{font:600 .8rem/1 "Outfit",sans-serif;letter-spacing:.14em;text-transform:uppercase;color:var(--green-deep);}
        [data-testid="abo-gefahrstoff-dev"] .gh-tag{font:600 .68rem/1 "Outfit",sans-serif;letter-spacing:.1em;text-transform:uppercase;color:#a2611a;background:#fdf1e2;border:1px solid #f4dcbf;border-radius:999px;padding:6px 11px;}
        [data-testid="abo-gefahrstoff-dev"] .gh-title{font-family:var(--font-display,"Outfit",sans-serif);font-weight:700;color:var(--ink);font-size:clamp(2rem,3.4vw,3rem);line-height:1.05;margin:0 0 12px;letter-spacing:-.01em;}
        [data-testid="abo-gefahrstoff-dev"] .gh-line{font:500 1.05rem/1.5 "Outfit",sans-serif;color:var(--body,#475569);margin:0;}

        /* Szenen-Abstände */
        [data-testid="abo-gefahrstoff-dev"] .gh-scene{margin-top:44px;}
        [data-testid="abo-gefahrstoff-dev"] .gh-scene-cap{margin:14px 0 0;font:500 .98rem/1.5 "Outfit",sans-serif;color:var(--body,#475569);max-width:640px;}
        [data-testid="abo-gefahrstoff-dev"] .gh-eyebrow{font:600 .72rem/1 "Outfit",sans-serif;letter-spacing:.14em;text-transform:uppercase;color:var(--green-deep);margin:0 0 10px;}

        /* Szene 2 · Detailbeweise (Lehrbilder + Verzeichnis-PDF) als gerahmte Karten */
        [data-testid="abo-gefahrstoff-dev"] .gh-proofs{display:grid;grid-template-columns:1fr;gap:22px;margin-top:18px;}
        [data-testid="abo-gefahrstoff-dev"] .gh-proof{background:#fff;border:1px solid #eef2f7;border-radius:18px;box-shadow:0 22px 48px rgba(15,40,80,.12);padding:14px;margin:0;overflow:hidden;}
        [data-testid="abo-gefahrstoff-dev"] .gh-proof img{display:block;width:100%;height:300px;object-fit:cover;object-position:top center;border-radius:11px;border:1px solid #f0f3f7;}
        [data-testid="abo-gefahrstoff-dev"] .gh-proof figcaption{margin:12px 4px 2px;font:600 .95rem/1.4 "Outfit",sans-serif;color:var(--ink);}
        [data-testid="abo-gefahrstoff-dev"] .gh-proof figcaption span{display:block;font-weight:500;color:var(--body,#64748b);font-size:.88rem;margin-top:3px;}

        /* Chris-Gruppe: Fig-Sonderhöhen dieser Bühne (Szenen nutzen frozen devb4/devb1) */
        [data-testid="abo-gefahrstoff-dev"] .gh-cap-head{font-family:var(--font-display,"Outfit",sans-serif);font-weight:700;color:var(--ink);font-size:clamp(1.4rem,2.2vw,1.9rem);line-height:1.1;margin:0 0 8px;letter-spacing:-.01em;}
        [data-testid="abo-gefahrstoff-dev"] .gh-cap-line{font:500 1rem/1.5 "Outfit",sans-serif;color:var(--body,#475569);margin:0;}

        @media(min-width:1000px){
          [data-testid="abo-gefahrstoff-dev"] .gh-proofs{grid-template-columns:1.05fr .95fr;gap:32px;}
          [data-testid="abo-gefahrstoff-dev"] .gh-proof img{height:360px;}
          [data-testid="abo-gefahrstoff-dev"] .gh-scene{margin-top:72px;}
        }
      `}</style>

      <div className="dev-label">Gefahrstoff-Bühne · Preview · Frozen Device-/Chris-Standard · Live /abo unberührt</div>

      <section className="gh-stage" data-testid="gh-stage">
        <div className="gh-wrap">
          {/* Kopf */}
          <header className="gh-head reveal">
            <div className="gh-meta">
              <span className="gh-kicker">Gefahrstoffe</span>
              <span className="gh-tag" data-testid="gh-zusatz-tag">Zusatzmodul</span>
            </div>
            <h2 className="gh-title" data-testid="gh-title">
              Ein Gefahrstoff. Die komplette Dokumentation.
            </h2>
            <p className="gh-line">
              Sie tippen den Namen. Den Rest übernimmt CLartext.
            </p>
          </header>

          {/* Szene 1 · Eingabe „Sterilium" + Chris 1 (Kette) — Monitor links, Chris rechts */}
          <div className="abo-devb4-grid gh-scene reveal" data-testid="gh-scene-suche">
            <figure className="abo-clmon abo-devb4-monitor" data-testid="gh-search-proof">
              <img
                src={DEV("device-gh-search.webp")}
                alt="CLartext auf einem hochwertigen Desktop-Gerät: Sterilium eingetippt – 1 Treffer, Sterillium Händedesinfektionsmittel als Dokumentation"
                data-testid="gh-shot-search"
                loading="eager"
                draggable="false"
              />
            </figure>
            <div className="abo-devb4-chris abo-chrisgrp" data-testid="gh-chris1-wrap">
              <AboChris
                pose={17}
                size="md"
                testId="gh-chris-1"
                text="Sie nennen mir den Gefahrstoff. Ich bereite die Unterweisung vor, mache die Inhalte verständlich, erstelle Ihr Gefahrstoffverzeichnis und halte es aktuell."
              />
            </div>
          </div>

          {/* Szene 2 · Was daraus entsteht — Detailbeweise (kein Chris) */}
          <div className="gh-scene reveal" data-testid="gh-scene-inhalte">
            <p className="gh-eyebrow">Was daraus entsteht</p>
            <div className="gh-proofs">
              <figure className="gh-proof" data-testid="gh-proof-lehrbilder">
                <img
                  src={SHOT("gh-lehrbilder-sterilium.webp")}
                  alt="CLartext Sterillium-Unterweisung: Sicherheitssymbole und Richtig/Falsch-Lehrbilder zum Umgang mit dem Händedesinfektionsmittel"
                  data-testid="gh-shot-lehrbilder"
                  loading="lazy"
                  draggable="false"
                />
                <figcaption>
                  Verständliche Lehrbilder & Sicherheitszeichen
                  <span>Aus dem Produkt automatisch erzeugt – nicht abstrakt, sondern gezeigt.</span>
                </figcaption>
              </figure>
              <figure className="gh-proof" data-testid="gh-proof-pdf">
                <img
                  src={SHOT("gh-verzeichnis-pdf-sterilium.webp")}
                  alt="CLartext Gefahrstoffverzeichnis als PDF nach § 6 Abs. 12 GefStoffV mit Sterillium classic pure (BODE Chemie), Spalten Bezeichnung, Hersteller, PSA, Mengenbereich, Arbeitsbereich, SDB-Belegnummer"
                  data-testid="gh-shot-pdf"
                  loading="lazy"
                  draggable="false"
                />
                <figcaption>
                  Gefahrstoffverzeichnis als PDF
                  <span>Prüffertig nach § 6 Abs. 12 GefStoffV – jederzeit exportierbar.</span>
                </figcaption>
              </figure>
            </div>
          </div>

          {/* Szene 3 · Verzeichnis lebt + SDB + Chris 2 — Monitor rechts, Chris links */}
          <div className="abo-devb1-grid gh-scene reveal" data-testid="gh-scene-verzeichnis">
            <div className="abo-devb1-copy" data-testid="gh-verz-copy">
              <p className="gh-eyebrow">Verzeichnis & Sicherheitsdatenblatt</p>
              <h3 className="gh-cap-head">Automatisch gepflegt. Beleg inklusive.</h3>
              <p className="gh-cap-line">
                Aus Ihren erfassten Gefahrstoffen erstellt CLartext automatisch das
                Gefahrstoffverzeichnis – übersichtlich dokumentiert und mit dem
                zugehörigen Sicherheitsdatenblatt verknüpft.
              </p>
            </div>
            <div className="abo-devb1-media" data-testid="gh-register-proof">
              <figure className="abo-clmon" style={{ margin: 0 }}>
                <img
                  src={DEV("device-gh-register.webp")}
                  alt="CLartext Gefahrstoffverzeichnis-UI auf einem hochwertigen Desktop-Gerät: Verzeichnis als PDF, Suche, Filter nach Einsatzbereich, Detail mit Substitutionsprüfung"
                  data-testid="gh-shot-register"
                  loading="lazy"
                  draggable="false"
                />
              </figure>
            </div>
            <div className="abo-devb1-chris abo-chrisgrp" data-testid="gh-chris2-wrap">
              <AboChris
                pose={23}
                size="md"
                testId="gh-chris-2"
                text="Das Sicherheitsdatenblatt suche ich selbstständig für Sie. Das Original speichere ich mit ab – für Ihre Dokumentation."
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
